import { FIELD_UPDATE_SOURCE } from "@common/constants";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import {
    getAllAccrualLineItemExtractions,
    mergeUpdatedAccrualExtraction,
    resolveAccrualRowExtraction,
} from "./accrualImporter.helpers";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";

export function getAccrualCounterpartyMutationKey(
    financialDocumentId: number,
    extractionId?: string,
) {
    return [
        "accrualImporterCounterparty",
        financialDocumentId,
        extractionId ?? "primary",
    ] as const;
}

export function getExtractionOrderIndex({
    financialDocument,
    extractionId,
    mode,
}: {
    financialDocument: FinancialDocumentExtended;
    extractionId: string;
    mode: AccrualDocumentImporterMode;
}): number | null {
    const extractions = getAllAccrualLineItemExtractions(
        financialDocument,
        mode,
    );
    const orderIndex = extractions.findIndex(
        (extraction) => extraction.id === extractionId,
    );

    return orderIndex >= 0 ? orderIndex : null;
}

export function buildDocumentWithCounterparty({
    financialDocument,
    counterpartyId,
    counterpartyName,
    mode,
    extractionId,
}: {
    financialDocument: FinancialDocumentExtended;
    counterpartyId: string | null;
    counterpartyName: string | null;
    mode: AccrualDocumentImporterMode;
    extractionId?: string;
}): FinancialDocumentExtended {
    const extraction = resolveAccrualRowExtraction(
        financialDocument,
        mode,
        extractionId,
    );

    if (!extraction) {
        return financialDocument;
    }

    const updatedExtraction = {
        ...extraction,
        counterpartyId,
        counterpartyName,
        counterpartyIdSource:
            counterpartyId === extraction.counterpartyId
                ? extraction.counterpartyIdSource
                : FIELD_UPDATE_SOURCE.ACTIONS,
    };

    if (mode === "bills") {
        return {
            ...financialDocument,
            documentBillsExtraction: financialDocument.documentBillsExtraction
                ? {
                      ...financialDocument.documentBillsExtraction,
                      billExtractions: mergeUpdatedAccrualExtraction({
                          allExtractions: getAllAccrualLineItemExtractions(
                              financialDocument,
                              "bills",
                          ),
                          updatedExtraction,
                      }),
                  }
                : null,
        };
    }

    return {
        ...financialDocument,
        documentInvoicesExtraction: financialDocument.documentInvoicesExtraction
            ? {
                  ...financialDocument.documentInvoicesExtraction,
                  invoiceExtractions: mergeUpdatedAccrualExtraction({
                      allExtractions: getAllAccrualLineItemExtractions(
                          financialDocument,
                          "invoices",
                      ),
                      updatedExtraction,
                  }),
              }
            : null,
    };
}
